import type { User } from "@/types/auth";

import { useAuth } from "@/contexts/AuthContext";
import {
  resolveDynamicFlag,
  useDynamicFlag,
} from "@/utils/featureFlagOverride";

export const MAINTENANCE_BANNER_FLAG_KEY = "VITE_FF_MAINTENANCE_BANNER";
export const MAINTENANCE_BANNER_REGULAR_USERS_ONLY_FLAG_KEY =
  "VITE_FF_MAINTENANCE_BANNER_REGULAR_USERS_ONLY";
export const MAINTENANCE_MODE_FLAG_KEY = "VITE_FF_MAINTENANCE_MODE";

export const DEFAULT_MAINTENANCE_BANNER_MESSAGE =
  "We have scheduled maintenance tonight. You will not be able to access the UI until maintenance is complete.";

export const DEFAULT_MAINTENANCE_MODE_MESSAGE =
  "5050 Club is temporarily unavailable while we perform scheduled maintenance. Please check back soon.";

const bannerEnabledEnv = import.meta.env.VITE_FF_MAINTENANCE_BANNER === "true";
const regularUsersOnlyEnv =
  import.meta.env.VITE_FF_MAINTENANCE_BANNER_REGULAR_USERS_ONLY !== "false";
const maintenanceModeEnvValue =
  import.meta.env.VITE_FF_MAINTENANCE_MODE === "true";

export const isMaintenanceBannerEnabled = () =>
  resolveDynamicFlag(MAINTENANCE_BANNER_FLAG_KEY, bannerEnabledEnv);

export const useIsMaintenanceBannerEnabled = () =>
  useDynamicFlag(MAINTENANCE_BANNER_FLAG_KEY, bannerEnabledEnv);

export const isMaintenanceBannerRegularUsersOnly = () =>
  resolveDynamicFlag(
    MAINTENANCE_BANNER_REGULAR_USERS_ONLY_FLAG_KEY,
    regularUsersOnlyEnv,
  );

export const useIsMaintenanceBannerRegularUsersOnly = () =>
  useDynamicFlag(
    MAINTENANCE_BANNER_REGULAR_USERS_ONLY_FLAG_KEY,
    regularUsersOnlyEnv,
  );

export const isAppUnderMaintenance = () =>
  resolveDynamicFlag(MAINTENANCE_MODE_FLAG_KEY, maintenanceModeEnvValue);

export const useIsAppUnderMaintenance = () =>
  useDynamicFlag(MAINTENANCE_MODE_FLAG_KEY, maintenanceModeEnvValue);

export function getMaintenanceBannerMessage(): string {
  const message = import.meta.env.VITE_FF_MAINTENANCE_BANNER_MESSAGE?.trim();
  return message || DEFAULT_MAINTENANCE_BANNER_MESSAGE;
}

export function getMaintenanceModeMessage(): string {
  const message = import.meta.env.VITE_FF_MAINTENANCE_MODE_MESSAGE?.trim();
  return message || DEFAULT_MAINTENANCE_MODE_MESSAGE;
}

export function shouldShowMaintenanceBannerForRole(
  role: User["role"] | null | undefined,
  regularUsersOnly: boolean,
): boolean {
  if (!role) return false;
  if (regularUsersOnly) return role === "user";
  return true;
}

export function isAppAccessBlockedByMaintenanceMode(
  role: User["role"] | null | undefined,
): boolean {
  return (
    isAppUnderMaintenance() && shouldShowMaintenanceBannerForRole(role, true)
  );
}

export function useIsAppAccessBlockedByMaintenanceMode(): boolean {
  const { user } = useAuth();
  return isAppAccessBlockedByMaintenanceMode(user?.user?.role);
}
